import { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

export function SignaturePad({ onSignatureChange, width = 400, height = 150, disabled = false }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const getCtx = () => canvasRef.current?.getContext("2d");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1a1a1a";
    // Fill white background
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    if (disabled) return;
    e.preventDefault();
    const ctx = getCtx();
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const ctx = getCtx();
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasContent(true);
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (hasContent && canvasRef.current) {
      onSignatureChange?.(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
    onSignatureChange?.(null);
  };

  return (
    <div data-testid="signature-pad-container">
      <div className={`border-2 ${disabled ? 'border-slate-200 bg-slate-50' : 'border-slate-300'} inline-block relative`}>
        <canvas ref={canvasRef} width={width} height={height}
          className="touch-none cursor-crosshair"
          style={{ width: "100%", maxWidth: `${width}px`, height: "auto", aspectRatio: `${width}/${height}` }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
          data-testid="signature-canvas" />
      </div>
      {!disabled && (
        <div className="flex items-center gap-3 mt-2">
          <Button type="button" size="sm" variant="outline" onClick={clear} className="rounded-full text-xs border h-7 px-3" data-testid="clear-signature-btn">
            <Eraser className="w-3 h-3 mr-1" />Clear
          </Button>
          <span className="text-[10px] text-muted-foreground">{hasContent ? "Signature captured" : "Draw your signature above"}</span>
        </div>
      )}
    </div>
  );
}
